import { ArrowRight, AudioLines, CheckCircle2, Paperclip, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { StatusPill } from "@/components/StatusPill";
import { InitialsAvatar } from "./landingPrimitives";

/* ============================================================
 * §1 · HERO — environmental canvas (approved wide artwork used
 * as the section's art-directed environment). No cream rectangle
 * behind the photograph, no card frame, no mask fog. Content is
 * live React over the image; a restrained left-side warm wash
 * keeps the headline column readable without producing a white
 * haze around the photograph.
 * ============================================================ */
export const HeroSection = ({ c }) => {
  return (
  <section
    id="features-hero"
    data-testid="features-hero"
    className="relative overflow-hidden lg:min-h-[960px]"
  >
    {/* Full-bleed environmental canvas — DESKTOP ONLY. Hidden on
        mobile to avoid overlapping the copy stack. The mobile
        breakpoint uses an in-flow image between copy and Martha
        interaction (see below), keeping every mobile line of
        copy on a clean sky/light background with zero overlays. */}
    <img
      src="/brand/features/hero_env_canvas_v7.png"
      alt="Levi at a contractor's worktable with tumbler, plans, tape measure, phone, laptop, hard hat and building code books; bright blue sky and a home behind him."
      draggable={false}
      className="hidden lg:block absolute inset-0 w-full h-full object-cover object-center select-none"
      style={{ zIndex: 0 }}
    />

    <div
      className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-8 lg:pt-8 lg:pb-24"
      style={{ zIndex: 2 }}
    >
      <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
        {/* LEFT · headline + copy + bullets + CTA. Compact vertical
            rhythm so the entire supporting-copy group lives in the
            bright upper-left portion of the environmental artwork
            (above the darker landscaping band). Live React
            typography only — no cream/beige box, no text-shadow. */}
        <div className="col-span-12 lg:col-span-5">
          <div>
            <h1 className="font-display leading-[1.02] tracking-tight text-[#0A1428] text-4xl sm:text-5xl lg:text-6xl">
              {c.hero.h1a}
              <br />
              <span className="text-[var(--jc-blue)]">{c.hero.h1b}</span>
            </h1>
            <p className="mt-4 text-[20px] leading-[1.5] font-semibold text-[#0A1428] max-w-md">
              {c.hero.lede}
            </p>
            <ul className="mt-5 space-y-2 text-[18px] font-semibold max-w-md">
              <li className="flex items-start gap-2.5">
                <CheckCircle2 size={22} className="text-[var(--jc-blue)] mt-0.5 flex-shrink-0" />
                <span className="text-[#0A1428]">{c.hero.bullet1}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 size={22} className="text-[var(--jc-blue)] mt-0.5 flex-shrink-0" />
                <span className="text-[#0A1428]">{c.hero.bullet2}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 size={22} className="text-[var(--jc-blue)] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-bold text-[#0A1428]">{c.hero.bullet3_title}</div>
                  <div className="text-[18px] font-semibold text-[#0A1428] mt-0.5 leading-snug max-w-[300px]">{c.hero.bullet3_sub}</div>
                </div>
              </li>
            </ul>
            <Link
              to="/schedule-a-demo"
              data-testid="hero-cta"
              className="mt-5 jc-btn-blue px-5 py-3 rounded-md font-semibold inline-flex items-center gap-2 shadow-lg"
            >
              {c.hero.cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* MOBILE ONLY · Dedicated Levi visual moment. Sits after
            the copy block + CTA. Uses the approved v7 canvas at a
            taller aspect on phones so Levi reads large and clean:
            full hat, both ears, both eyes, muzzle, collar, Levi
            tag, chest and paws all in frame. No copy, no CTA, no
            Martha UI on top of him. */}
        <div className="col-span-12 lg:hidden mt-10">
          <div className="w-full h-[480px] rounded-2xl overflow-hidden shadow-xl bg-[#0B1E3E]">
            <img
              src="/brand/features/hero_env_canvas_v7.png"
              alt="Levi at a contractor's worktable — full hat, ears, eyes, collar and Levi tag visible; tumbler and laptop in the foreground; house behind."
              draggable={false}
              className="w-full h-full object-cover select-none"
              style={{ objectPosition: "62% 42%", transform: "scale(1.15)", transformOrigin: "62% 50%" }}
            />
          </div>
        </div>

        {/* Middle · reserved for the environmental composition
            (Levi + desk in the canvas). No overlay so the
            photograph remains fully visible. */}
        <div className="hidden lg:block lg:col-span-3" aria-hidden />

        {/* RIGHT · Martha interaction stack — narrow, top-aligned
            per PO wide-reference geometry. Compact ~340px column
            hugged flush right (ml-auto) so it stays in the far
            right zone and never crosses Levi's silhouette. */}
        <div className="col-span-12 lg:col-span-4 lg:ml-auto flex flex-col gap-4 lg:mt-0 mt-10 w-full lg:max-w-[300px]">
          {/* Enlarged voice bubble — Hero-only inline variant with
              the PO-preferred prominent blue waveform. Larger 44px
              tile with a 26px AudioLines glyph, subtle blue border
              accent, 14.5px command text. Sits in the right-side
              interaction zone, clear of Levi. */}
          <div
            data-testid="hero-martha-voice"
            className="relative rounded-2xl bg-[#0B1E3E] text-white shadow-xl px-4 py-3.5 flex items-center gap-3.5 border border-[var(--jc-blue)]/40"
          >
            <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-[var(--jc-blue)]/20 grid place-items-center text-[var(--jc-blue)]">
              <AudioLines size={26} strokeWidth={2.6} />
            </span>
            <span className="text-[14.5px] leading-snug">{c.hero.martha_voice}</span>
          </div>
          <div
            className="rounded-2xl bg-white shadow-2xl border border-white/50 p-4"
            data-testid="hero-martha-card"
          >
            <div className="flex items-start gap-3">
              <InitialsAvatar initials="MJ" size={36} testid="hero-martha-avatar" />
              <div className="text-[13.5px] leading-snug">
                {c.hero.martha_reply_pre}
                <br />
                <span className="font-semibold text-[var(--jc-text)]">{c.hero.martha_name}</span>
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-[var(--jc-border)] p-3">
              <div className="flex items-center gap-2 pb-2 border-b border-[var(--jc-border)]/70">
                <Paperclip size={14} className="text-[var(--jc-blue)]" />
                <span className="font-semibold text-[13px] flex-1">{c.hero.inv_label}</span>
                <span className="font-semibold text-[13px]">{c.hero.inv_amount}</span>
              </div>
              <div className="pt-2 text-[12.5px] leading-snug text-[var(--jc-text)]">
                {c.hero.inv_body_line1}
                <br />
                {c.hero.inv_body_line2}
              </div>
              <div className="mt-2 pt-2 border-t border-[var(--jc-border)]/70">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-[var(--jc-text-soft)] font-semibold">
                  <span>{c.hero.inv_email_label}</span>
                  <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.hero.inv_status_sent : k)} />
                </div>
                <div className="text-[12.5px] mt-1 text-[var(--jc-text)]">{c.hero.inv_email_addr}</div>
                <div className="text-[11.5px] text-[var(--jc-text-soft)]">{c.hero.inv_email_meta}</div>
              </div>
              <button
                type="button"
                className="mt-3 w-full border border-[var(--jc-border-strong)] rounded-md py-2 text-[12.5px] font-semibold text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
              >
                {c.hero.inv_view}
              </button>
            </div>
          </div>
          <div
            className="inline-flex items-center gap-2 rounded-full bg-[var(--jc-navy)] text-white px-4 py-2 text-[13px] font-semibold self-start shadow-xl"
            data-testid="hero-recommends"
          >
            <ShieldCheck size={15} className="text-[var(--jc-blue)]" />
            {c.hero.recommends}
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};
